import Select from 'ol/interaction/Select';
import Modify from 'ol/interaction/Modify';
import Translate from 'ol/interaction/Translate';

/**
 * OpenLayers 인터랙션 설정을 담당하는 클래스
 */
export class InteractionSetupManager {
  /**
   * 수정 인터랙션 설정
   * @param {ol/source/Vector} source - 벡터 소스
   * @param {Map} imagePolygonPairs - 이미지-폴리곤 쌍들
   * @param {Function} styleFunction - 스타일 함수
   * @returns {ol/interaction/Modify} 수정 인터랙션
   */
  setupModifyInteraction(source, imagePolygonPairs, styleFunction) {
    const modifyInteraction = new Modify({
      source: source,
      style: styleFunction
    });

    // 수정 시작 이벤트
    modifyInteraction.on('modifystart', (event) => {
      console.log('=== 수정 인터랙션 시작 ===');

      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          // 수정 중 상태로 설정
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'modifying');

          // 원본 정보 저장
          if (imagePolygonPair.fixedCenterModificationHandler) {
            imagePolygonPair.fixedCenterModificationHandler.storeOriginalGeometry(feature);
          }
        }
      });
    });

    // 수정 중 이벤트
    modifyInteraction.on('modify', (event) => {
      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          const geometry = feature.getGeometry();

          // 고정 중심점 수정 적용
          if (imagePolygonPair.fixedCenterModificationHandler) {
            const coordinates = geometry.getCoordinates()[0];
            imagePolygonPair.fixedCenterModificationHandler.applyFixedCenterModification(feature, coordinates);
          }

          // 이미지와 폴리곤 동기화
          imagePolygonPair.synchronizeImageWithPolygon(geometry);
        }
      });
    });

    // 수정 완료 이벤트
    modifyInteraction.on('modifyend', (event) => {
      console.log('=== 수정 인터랙션 완료 ===');

      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          // 기본 상태로 복원
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'default');

          // 수정 정보 정리
          if (imagePolygonPair.fixedCenterModificationHandler) {
            imagePolygonPair.fixedCenterModificationHandler.cleanupModification(feature);
          }

          // 최종 동기화
          const geometry = feature.getGeometry();
          imagePolygonPair.synchronizeImageWithPolygon(geometry);
        }
      });
    });

    console.log('InteractionSetupManager: 수정 인터랙션 설정 완료');
    return modifyInteraction;
  }

  /**
   * 이동 인터랙션 설정
   * @param {ol/source/Vector} source - 벡터 소스
   * @param {Map} imagePolygonPairs - 이미지-폴리곤 쌍들
   * @returns {ol/interaction/Translate} 이동 인터랙션
   */
  setupTranslateInteraction(source, imagePolygonPairs) {
    const translateInteraction = new Translate({
      source: source
    });

    // 이동 시작 이벤트
    translateInteraction.on('translatestart', (event) => {
      console.log('=== 이동 인터랙션 시작 ===');

      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          // 이동 중 상태로 설정
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'modifying');
        }
      });
    });

    // 이동 중 이벤트
    translateInteraction.on('translate', (event) => {
      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          const geometry = feature.getGeometry();

          // 이미지와 폴리곤 동기화
          imagePolygonPair.synchronizeImageWithPolygon(geometry);
        }
      });
    });

    // 이동 완료 이벤트
    translateInteraction.on('translateend', (event) => {
      console.log('=== 이동 인터랙션 완료 ===');

      event.features.getArray().forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          // 기본 상태로 복원
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'default');

          // 최종 동기화
          const geometry = feature.getGeometry();
          imagePolygonPair.synchronizeImageWithPolygon(geometry);
        }
      });
    });

    console.log('InteractionSetupManager: 이동 인터랙션 설정 완료');
    return translateInteraction;
  }

  /**
   * 선택 인터랙션 설정
   * @param {ol/source/Vector} source - 벡터 소스
   * @param {Map} imagePolygonPairs - 이미지-폴리곤 쌍들
   * @param {Function} styleFunction - 스타일 함수
   * @returns {ol/interaction/Select} 선택 인터랙션
   */
  setupSelectInteraction(source, imagePolygonPairs, styleFunction) {
    const selectInteraction = new Select({
      source: source,
      style: styleFunction
    });

    // 선택 변경 이벤트
    selectInteraction.on('select', (event) => {
      // 선택 해제된 피처들
      event.deselected.forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'default');
        }
      });

      // 새로 선택된 피처들
      event.selected.forEach(feature => {
        const imageId = feature.get('imageId');
        const imagePolygonPair = imagePolygonPairs.get(imageId);

        if (imagePolygonPair) {
          imagePolygonPair.styleManager.applyStateToFeature(feature, 'selected');
        }
      });
    });

    console.log('InteractionSetupManager: 선택 인터랙션 설정 완료');
    return selectInteraction;
  }

  /**
   * 모든 인터랙션 제거
   * @param {ol/Map} map - OpenLayers 맵
   * @param {Array} interactions - 제거할 인터랙션들
   */
  removeAllInteractions(map, interactions = []) {
    if (!map) return;

    interactions.forEach(interaction => {
      if (interaction && map.hasInteraction(interaction)) {
        map.removeInteraction(interaction);
      }
    });

    console.log('InteractionSetupManager: 모든 인터랙션 제거 완료');
  }

  /**
   * 인터랙션 활성화/비활성화
   * @param {ol/interaction/Interaction} interaction - 인터랙션
   * @param {boolean} active - 활성화 여부
   */
  setInteractionActive(interaction, active) {
    if (interaction) {
      interaction.setActive(active);
      console.log(`InteractionSetupManager: 인터랙션 ${active ? '활성화' : '비활성화'}`);
    }
  }
}
